/* What the page knows, beside the thing it does.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * On a laptop the player was a 720px column with nine hundred pixels of empty
 * green to the right of it. Empty space can be composure or it can be an
 * unfinished screen, and the difference is whether anything would have been
 * worth putting there. Here there was: the very first line of the brief --
 * "det de trenger a vite er 1) se hvor hydrofonen er deployet" -- had no home
 * on the page at all.
 *
 * ---------------------------------------------------------------------------
 * AND WHY IT IS SHORT
 *
 * The first build of it carried four blocks and fourteen values -- model,
 * serial, depth, deployed-since, hours stored, newest sound, segment length,
 * locality, position -- and every one of them was true and on the wire. It
 * still had to be cut in half, because a column of facts beside a player
 * competes with the player: the eye reads the dense thing first, and the dense
 * thing was not the point of the page. Anything already said elsewhere went
 * (the chip's dot says it is sending, the strip's caption says how many hours
 * are stored), and anything a farmer would never ask went with it.
 *
 * What is left is the question the brief opened with and the one thing here
 * that is a control rather than a readout.
 *
 * ---------------------------------------------------------------------------
 * ONLY FACTS THAT ARE ALREADY ON THE WIRE
 *
 * Every line here comes from `/v1/sites/{id}` or `/day`, both of which this
 * page already fetches. Nothing new is requested and nothing is inferred.
 *
 * Which rules out most of what a panel like this usually carries, and the
 * exclusions are the point:
 *
 *   no temperature, no current, no weather -- this system does not measure any
 *   of them, and a farmer who read "6 C" here would reasonably believe the
 *   hydrophone had told us
 *
 *   no detections -- the detector marks 98% of every minute on 50 Hz mains hum
 *   (phoniq-receiver/reports/detector-saturation-5ab3dec.md). A count from it
 *   would be a confident number that means nothing
 *
 *   no levels, in dB or otherwise -- ground rule 5. Uncalibrated is relative,
 *   so the panel says that in words instead of printing a figure that would
 *   look like a measurement of the sea
 *
 * An empty column is better than a full one that is guessing.
 */
import type { SiteDetail, SiteMission } from '../api'
import { useT } from '../lang'
import { clock } from '../time'
import type { DayClip } from './DayTimeline'

export function ListenAside({
  site, mission, name, clips, onOpenClip,
}: {
  site: SiteDetail
  mission: SiteMission | undefined
  /** What the sensor strip calls this post, so the two cannot disagree. */
  name: string
  clips: DayClip[]
  onOpenClip: (c: DayClip) => void
}) {
  const t = useT()

  return (
    <aside className="aside">
      {/* Which box you are listening to.
          Here rather than only on the sensor strip, because the strip is not
          rendered at all when a farm has one post -- which is almost every
          farm. Without this the name painted on the enclosure would appear
          nowhere in the product for the ordinary case. */}
      <section className="aside-block">
        <h2 className="label">{t('The listening post')}</h2>
        <p className="aside-lead">{name}</p>
        <p className="aside-sub">
          {[
            mission?.microphone_type ?? null,
            mission?.microphone_serial ?? null,
            mission?.deploy_depth_m != null
              ? `${mission.deploy_depth_m} m ${t('deep')}` : null,
          ].filter(Boolean).join(' · ')}
        </p>
      </section>

      {/* Where it is. The first line of the brief -- see where the listening
          post is deployed -- and until now the one thing on it with no home
          anywhere in the product. */}
      <section className="aside-block">
        <h2 className="label">{t('Where')}</h2>
        {site.lat != null && site.lon != null && (
          <p className="aside-lead num">
            {site.lat.toFixed(4)}, {site.lon.toFixed(4)}
          </p>
        )}
        {site.locality_no != null && (
          <p className="aside-sub">{t('Locality')} {site.locality_no}</p>
        )}
      </section>

      {/* The clips, as a list rather than only as marks a few pixels wide on a
          twenty-four-hour bar. Somebody who recorded something wants to find it
          again, and a mark you have to hunt for is not a way to find
          anything. */}
      <section className="aside-block">
        <h2 className="label">{t('Clips')}</h2>
        {clips.length ? (
          <ul className="aside-clips">
            {clips.map((c) => {
              const at = new Date(c.started_utc).getTime() / 1000
              return (
                <li key={c.clip_id}>
                  <button type="button" className="aside-clip"
                    onClick={() => onOpenClip(c)}>
                    <span className="num">{clock(at)}</span>
                    <span className="muted num">{Math.round(c.duration_s)} s</span>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="aside-none">{t('None this day.')}</p>
        )}
      </section>

      {/* No calibration prose here any more.
          It said what the numbers rest on and what the report's marks are and
          are not, and all of it was true -- and it was four hundred characters
          of physics in the column beside a play button, which is not what this
          column is for. The customer is told how to read these numbers by the
          people who sell them the instrument; the page's job is to be the
          instrument.

          The declaration itself has not gone. The scale still prints its unit
          -- `dB re 1 uPa` -- and still carries the ANSLAG badge next to the
          number, which is the basis R5.3 requires stated, in two words instead
          of five sentences. What went was the explanation around it. */}
    </aside>
  )
}

